import React, { useCallback } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import type { CalendarPost, CalendarSong } from '../types';
import { calendarStateOf } from './calendarFilterModel';
import { PostThumb } from './PostThumb';
import { PostStateMark } from './PostStateMark';
import { postCardLabel, postTime } from './postCardLabel';
import { postTitle } from './calendarPostView';
import type { DragMove } from './useDragMove';
import { useDisplayPostStatus } from '../displayPostStatus';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Calendar; each part uses its own slice of it */
import styles from './PromoteCalendar.module.scss';
/* eslint-enable css-modules/no-unused-class */

const STATE_CLASS = {
  needsYou: styles.stManual,
  failed: styles.stFailed,
  draft: styles.stDraft,
  scheduled: undefined,
  published: styles.stPosted,
  canceled: styles.stCanceled,
} as const;

interface CalendarPostCardProps {
  post: CalendarPost;
  song: CalendarSong | undefined;
  drag: DragMove;
  isSelected?: boolean;
  onOpen: (post: CalendarPost) => void;
}

/**
 * One compact card (design a1): the media thumbnail with the platform tile, the time, the song and
 * the state mark. A click or Enter opens the day panel on the post. Space picks a movable card up
 * (`useDragMove`).
 */
const CalendarPostCardComponent: React.FC<CalendarPostCardProps> = ({ post, song, drag, isSelected, onOpen }) => {
  const intl = useIntl();
  const handlers = drag.cardHandlers(post);
  const isHeld = drag.heldUuid === post.uuid;
  // A scheduled post whose time has passed is going out: it shows as publishing.
  const shownStatus = useDisplayPostStatus(post);

  const open = useCallback(() => onOpen(post), [onOpen, post]);
  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      handlers.onKeyDown?.(event);
      if (!event.defaultPrevented && event.key === 'Enter') {
        event.preventDefault();
        open();
      }
    },
    [handlers, open]
  );

  return (
    <div
      role="button"
      tabIndex={0}
      className={clsx(
        styles.pc,
        STATE_CLASS[calendarStateOf(post.status)],
        isHeld && styles.pcHeld,
        isSelected === true && styles.pcSelected
      )}
      aria-label={postCardLabel(intl, { ...post, status: shownStatus }, song)}
      aria-pressed={isSelected === true ? true : undefined}
      data-testid={`calendar-post-${post.uuid}`}
      draggable={handlers.draggable}
      onDragStart={handlers.onDragStart}
      onDragEnd={handlers.onDragEnd}
      onClick={open}
      onKeyDown={onKeyDown}
    >
      <PostThumb post={post} song={song} />
      <span className={styles.tm}>{postTime(intl, post)}</span>
      <span className={styles.sg}>{postTitle(post, song)}</span>
      <PostStateMark status={shownStatus} />
    </div>
  );
};

export const CalendarPostCard = TypedMemo(CalendarPostCardComponent);
